import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
// import TributeViewer from './TributeViewer';
import PaymentModal from './PaymentModal';

const candleImages = [
    'spokojna-pamięć.jpg',
    'krzyż-zadumy.jpg',
    'ciepło-chryzantem.jpg',
    'czerwień-serca.jpg',
    'prosty-glamour.jpg',
    'spokój-w-kaplicy.jpg',
    'trójpłomień-nadziei.jpg',
    'glamour.jpg',
    'płomień-nadziei.jpg',
    'okno-pamięci.jpg',
    'vintage.jpg',
];

const formatCandleName = (filename) => {
    const name = filename.replace(/\.jpg$/, '').replace(/-/g, ' ');
    return name.charAt(0).toUpperCase() + name.slice(1);
};

export const candleTypes = candleImages.map(filename => ({
    id: filename.replace(/\.jpg$/, ''),
    name: formatCandleName(filename),
    image: `/candles/${filename}`,
}));

function TributeCreator() {
    const [personName, setPersonName] = useState('');
    const [memorialMessage, setMemorialMessage] = useState('');
    const [fromName, setFromName] = useState('');
    const [searchParams] = useSearchParams();
    const bucketParam = parseInt(searchParams.get('bucket'), 10);
    const initialCandle = (bucketParam >= 1 && bucketParam <= 11) ? candleTypes[bucketParam - 1] : candleTypes[0];

    const [selectedCandle, setSelectedCandle] = useState(initialCandle);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [createdTributeId, setCreatedTributeId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleCreateTribute = useCallback(async () => {
        if (!executeRecaptcha) {
            console.error('Execute recaptcha not yet available');
            setError('reCAPTCHA nie jest gotowa, spróbuj ponownie za chwilę.');
            return;
        }

        setIsLoading(true);
        setError('');
        setCreatedTributeId(null);

        try {
            const recaptchaToken = await executeRecaptcha('submit');

            const tributeData = {
                recipientName: personName,
                memorialMessage: memorialMessage,
                senderName: fromName,
                candleNumber: selectedCandle.id,
                recaptchaToken: recaptchaToken,
            };

            const response = await fetch('/api/tributes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(tributeData),
            });

            if (!response.ok) {
                let errorMessage = `Błąd HTTP! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (e) {
                    // Ignore if response is not JSON or empty
                }
                if (response.status === 400 && errorMessage.toLowerCase().includes('recaptcha')) {
                    errorMessage = "Weryfikacja reCAPTCHA nie powiodła się. Odśwież stronę i spróbuj ponownie.";
                } else if (response.status === 403) {
                    errorMessage = "Usługa niedostępna w Twoim kraju.";
                }
                throw new Error(errorMessage);
            }

            const createdTribute = await response.json();
            if (!createdTribute || !createdTribute.id) {
                throw new Error('Nieprawidłowa odpowiedź serwera: Brak ID znicza.');
            }

            setCreatedTributeId(createdTribute.id);
            setIsPaymentModalOpen(true);

        } catch (e) {
            console.error('Error creating tribute:', e);
            setError(e.message || 'Nie udało się utworzyć znicza. Spróbuj ponownie.');
        } finally {
            setIsLoading(false);
        }
    }, [executeRecaptcha, personName, memorialMessage, fromName, selectedCandle.id]);

    // Aby przywrócić podgląd znicza po prawej stronie:
    // 1. Usuń style z elementu <div id="tribute-creator" className="main-content-split">
    // 2. Odkomentuj import TributeViewer
    // 3. Odkomentuj blok <TributeViewer ... /> poniżej
    return (
        <div
            id="tribute-creator"
            className="main-content-split"
            style={{
                gridTemplateColumns: '1fr',
                maxWidth: '800px',
                margin: '0 auto',
                width: '100%',
            }}
        >
            <div className="tribute-creator">
                <div className="form-section">
                    <h2>Stwórz swój wirtualny znicz</h2>
                    <div className="input-group">
                        <label htmlFor="personName">Ku pamięci:</label>
                        <input
                            type="text"
                            id="personName"
                            value={personName}
                            onChange={(e) => setPersonName(e.target.value)}
                            maxLength="20"
                        />
                    </div>
                    <div className="input-group">
                        <label htmlFor="memorialMessage">Wiadomość pamiątkowa</label>
                        <textarea
                            id="memorialMessage"
                            value={memorialMessage}
                            onChange={(e) => setMemorialMessage(e.target.value)}
                            rows="3"
                            maxLength="150"
                        />
                    </div>
                    <div className="input-group">
                        <label htmlFor="fromName">Od kogo:</label>
                        <input
                            type="text"
                            id="fromName"
                            value={fromName}
                            onChange={(e) => setFromName(e.target.value)}
                            maxLength="20"
                        />
                    </div>
                </div>

                <div className="candle-selection">
                    <h2>Wybierz znicz</h2>
                    <div className="candle-options">
                        {candleTypes.map((candle) => (
                            <div
                                key={candle.id}
                                className={`candle-option ${selectedCandle.id === candle.id ? 'selected' : ''}`}
                                onClick={() => setSelectedCandle(candle)}
                            >
                                <img src={candle.image} alt={candle.name}/>
                                <span>{candle.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {error && <p style={{color: 'red', textAlign: 'center'}}>Błąd: {error}</p>}
                <button
                    className="btn"
                    style={{ marginTop: '1.5rem' }}
                    onClick={handleCreateTribute}
                    disabled={isLoading || !executeRecaptcha}
                >
                    {isLoading ? 'Tworzenie...' : 'Zapal znicz'}
                </button>
            </div>

            {/* <TributeViewer
                title="Podgląd Twojego znicza"
                personName={personName}
                memorialMessage={memorialMessage}
                fromName={fromName}
                selectedCandle={selectedCandle}
            /> */}

            {isPaymentModalOpen && createdTributeId && (
                <PaymentModal
                    tributeId={createdTributeId}
                    onClose={() => setIsPaymentModalOpen(false)}
                />
            )}
        </div>
    );
}

export default TributeCreator;
